import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updateVolumePriceFromQuantity);
    document.addEventListener(ThemeEvents.cartUpdate, this.updateVolumePriceFromQuantity);
    this.updateVolumePriceFromQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.updateVolumePriceFromQuantity);
    document.removeEventListener(ThemeEvents.cartUpdate, this.updateVolumePriceFromQuantity);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    this.#syncDatasets(newProductPrice);

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateVolumePriceFromQuantity();
  };

  updateVolumePriceFromQuantity = (event) => {
    if (this.dataset.hasVolumePricing !== 'true') return;
    if (event?.type === ThemeEvents.quantitySelectorUpdate && !this.#isOwnQuantityEvent(event)) return;
    if (!this.refs.priceContainer) return;

    const priceElement = this.refs.priceContainer.querySelector('.price');
    if (!priceElement) return;

    const activePrice = this.#getActiveVolumePrice();
    const moneyFormat = this.dataset.moneyFormat;
    const currencyCode = this.dataset.currencyCode;

    if (!moneyFormat || !currencyCode || activePrice === null) return;

    priceElement.textContent = formatMoney(activePrice, moneyFormat, currencyCode);
  };

  #syncDatasets(newProductPrice) {
    for (const attribute of [
      'data-base-price',
      'data-currency-code',
      'data-has-volume-pricing',
      'data-min-quantity',
      'data-money-format',
      'data-price-breaks',
      'data-product-id',
    ]) {
      const value = newProductPrice.getAttribute(attribute);

      if (value === null) {
        this.removeAttribute(attribute);
      } else {
        this.setAttribute(attribute, value);
      }
    }
  }

  #isOwnQuantityEvent(event) {
    const productForm = this.#getProductForm();
    return Boolean(productForm && event.target instanceof Node && productForm.contains(event.target));
  }

  #getProductForm() {
    const container = this.closest('.shopify-section, dialog');
    if (!container || !this.dataset.productId) return null;

    return container.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  #getCurrentQuantity() {
    const productForm = this.#getProductForm();
    const quantityInput = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="quantity"]'));
    const minimumQuantity = parseInt(this.dataset.minQuantity || '1', 10) || 1;

    if (!quantityInput) return minimumQuantity;

    const cartQuantity = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0;
    const inputQuantity = parseInt(quantityInput.value || `${minimumQuantity}`, 10) || minimumQuantity;

    return cartQuantity + inputQuantity;
  }

  #getActiveVolumePrice() {
    const basePrice = parseInt(this.dataset.basePrice || '', 10);
    if (Number.isNaN(basePrice)) return null;

    let activePrice = basePrice;
    const currentQuantity = this.#getCurrentQuantity();
    const rawPriceBreaks = this.dataset.priceBreaks;

    if (!rawPriceBreaks) return activePrice;

    try {
      const priceBreaks = JSON.parse(rawPriceBreaks);

      for (const priceBreak of priceBreaks) {
        if (currentQuantity >= Number(priceBreak.quantity)) {
          activePrice = Number(priceBreak.price) || activePrice;
        }
      }
    } catch (error) {
      console.error('Failed to parse volume pricing data:', error);
    }

    return activePrice;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
